import React, { useState, useCallback } from 'react';
import { ValidCell } from '../types';
import { BUILDINGS, GRID } from '../constants';
import { motion, AnimatePresence } from 'framer-motion';
import ScoreTooltip from './ScoreTooltip';

interface InteractiveGridProps {
    board: { [key in ValidCell]?: 'A' | 'B' };
    onCellClick: (position: ValidCell) => void;
    selectedPosition?: ValidCell | null;
    teamAPositions: ValidCell[];
    teamBPositions: ValidCell[];
    competitorPositions: ValidCell[];
    currentTeam?: 'A' | 'B';
    readOnly?: boolean;
    blockedPositions?: ValidCell[];
}

const getBuildingIcon = (score: number) => {
    if (score === 60) return '🏢'; // Gold skyscraper
    if (score === 48) return '🏬'; // Silver building
    if (score === 24) return '🏪'; // Bronze shop
    return '🏠'; // Basic structure
};

const getBuildingClass = (score: number) => {
    if (score === 60) return 'badge-gold';
    if (score === 48) return 'badge-silver';
    if (score === 24) return 'badge-bronze';
    return 'badge-basic';
};

const InteractiveGrid: React.FC<InteractiveGridProps> = ({
    board,
    onCellClick,
    selectedPosition,
    teamAPositions,
    teamBPositions,
    competitorPositions,
    currentTeam,
    readOnly = false,
    blockedPositions = []
}) => {
    const [hoveredCell, setHoveredCell] = useState<ValidCell | null>(null);
    const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

    const handleCellHover = useCallback((cell: ValidCell | null, event?: React.MouseEvent) => {
        setHoveredCell(cell);
        if (event && cell) {
            const rect = event.currentTarget.getBoundingClientRect();
            setTooltipPosition({
                x: rect.left + rect.width / 2,
                y: rect.top - 10
            });
        }
    }, []);

    const handleCellClick = useCallback((cell: ValidCell) => {
        if (readOnly) return;
        if (board[cell]) return; // Already occupied
        if (competitorPositions.includes(cell)) return; // Blocked by competitor
        if (blockedPositions.includes(cell)) return; // Temporarily blocked

        onCellClick(cell);
    }, [readOnly, board, competitorPositions, blockedPositions, onCellClick]);

    const getCellClass = (cell: string): string => {
        const validCell = cell as ValidCell;
        const classes: string[] = ['cell'];

        // Check if it's a playable cell
        if (cell && cell.match(/^[MKDLW][1-5]$/)) {
            // Occupied states
            if (board[validCell] === 'A') {
                classes.push('cell-occupied-a');
            } else if (board[validCell] === 'B') {
                classes.push('cell-occupied-b');
            }
            // Blocked by competitor
            else if (competitorPositions.includes(validCell)) {
                classes.push('cell-blocked');
            }
            // Selected state
            else if (selectedPosition === validCell) {
                classes.push('cell-selected');
            }
            // Temporarily blocked due to conflict
            else if (blockedPositions.includes(validCell)) {
                classes.push('cell-conflict');
            }
            // Hovering
            else if (hoveredCell === validCell && !readOnly) {
                classes.push('cell-hover');
            }
            // Default playable
            else {
                classes.push('cell-playable');
            }
        } else if (cell) {
            classes.push('cell-building');
        }

        return classes.join(' ');
    };

    const isPlayableCell = (cell: string): boolean => {
        return cell !== '' && cell.match(/^[MKDLW][1-5]$/) !== null;
    };

    return (
        <div className="grid-container" style={{ position: 'relative' }}>
            <div className="grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(9, 1fr)',
                gap: 'var(--grid-gap)',
                padding: 'var(--space-lg)',
                background: 'var(--bg-secondary)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-xl)'
            }}>
                {GRID.map((row, rowIndex) => (
                    row.map((cell, colIndex) => {
                        const key = `${rowIndex}-${colIndex}`;
                        const building = BUILDINGS[cell as keyof typeof BUILDINGS];

                        if (isPlayableCell(cell)) {
                            const validCell = cell as ValidCell;
                            return (
                                <motion.div
                                    key={key}
                                    className={getCellClass(cell)}
                                    onClick={() => handleCellClick(validCell)}
                                    onMouseEnter={(e) => handleCellHover(validCell, e)}
                                    onMouseLeave={() => handleCellHover(null)}
                                    whileHover={!readOnly && !board[validCell] ? { scale: 1.05 } : {}}
                                    whileTap={!readOnly && !board[validCell] ? { scale: 0.95 } : {}}
                                    style={{
                                        minHeight: '60px',
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        fontFamily: 'JetBrains Mono, monospace'
                                    }}
                                >
                                    {board[validCell] && (
                                        <motion.span
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            transition={{ type: 'spring', stiffness: 500, damping: 15 }}
                                        >
                                            {validCell}
                                        </motion.span>
                                    )}
                                    {!board[validCell] && selectedPosition === validCell && (
                                        <motion.span
                                            animate={{ opacity: [0.5, 1, 0.5] }}
                                            transition={{ duration: 1.5, repeat: Infinity }}
                                            style={{ fontSize: '1.5rem' }}
                                        >
                                            {currentTeam === 'A' ? '🔵' : '🔴'}
                                        </motion.span>
                                    )}
                                    {!board[validCell] && selectedPosition !== validCell && (
                                        <span style={{ opacity: 0.5 }}>{validCell}</span>
                                    )}
                                </motion.div>
                            );
                        } else if (building) {
                            // Building cell
                            return (
                                <div
                                    key={key}
                                    className="cell cell-building"
                                    style={{
                                        minHeight: '60px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '4px'
                                    }}
                                >
                                    <div style={{ fontSize: '1.5rem' }}>
                                        {getBuildingIcon(building.score)}
                                    </div>
                                    <div className={`badge ${getBuildingClass(building.score)}`}>
                                        {cell}
                                    </div>
                                    <div style={{
                                        fontSize: '0.75rem',
                                        color: 'var(--text-muted)',
                                        fontWeight: '700'
                                    }}>
                                        {building.score}pts
                                    </div>
                                </div>
                            );
                        } else {
                            // Empty cell
                            return <div key={key} className="cell" style={{ minHeight: '60px' }} />;
                        }
                    })
                ))}
            </div>

            {/* Tooltip */}
            <AnimatePresence>
                {hoveredCell && isPlayableCell(hoveredCell) && !board[hoveredCell] && (
                    <ScoreTooltip
                        position={hoveredCell}
                        x={tooltipPosition.x}
                        y={tooltipPosition.y}
                        currentTeam={currentTeam}
                        teamAPositions={teamAPositions}
                        teamBPositions={teamBPositions}
                        competitorPositions={competitorPositions}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default InteractiveGrid;
